/**
 * The reduced API portal (`portal_mode = api`): its menu and the routes it may
 * open. Both live here so the sidebar and the route guard cannot drift: a page
 * in the menu is always reachable, and nothing outside it is.
 */
import {
  mdiAccountGroupOutline,
  mdiBookOpenVariant,
  mdiChartBar,
  mdiDatabaseCheckOutline,
  mdiFileDocumentOutline,
  mdiKeyOutline,
  mdiStorefrontOutline,
  mdiViewDashboardOutline,
} from '@mdi/js';

export const API_PORTAL_HOME = '/api';

/**
 * Menu entries. `adminOnly` rows are shown to tenant admins only (the members
 * page manages seats). `activeMatch` follows SidebarMenuItem.
 */
export const API_PORTAL_NAV_ITEMS = Object.freeze([
  { to: API_PORTAL_HOME, labelKey: 'api_store.nav.overview', icon: mdiViewDashboardOutline, activeMatch: 'exact' },
  { to: '/api/store', labelKey: 'api_store.nav.store', icon: mdiStorefrontOutline, activeMatch: 'prefix' },
  { to: '/api/access', labelKey: 'api_store.nav.access', icon: mdiDatabaseCheckOutline, activeMatch: 'prefix' },
  { to: '/account/api-keys', labelKey: 'api_store.nav.keys', icon: mdiKeyOutline, activeMatch: 'prefix' },
  { to: '/api/usage', labelKey: 'api_store.nav.usage', icon: mdiChartBar, activeMatch: 'prefix' },
  {
    to: '/platform/subscription/invoices',
    labelKey: 'api_store.nav.invoices',
    icon: mdiFileDocumentOutline,
    activeMatch: 'prefix',
  },
  { to: '/api-docs-auth', labelKey: 'api_store.nav.docs', icon: mdiBookOpenVariant, activeMatch: 'prefix' },
  {
    to: '/platform/subscription/members',
    labelKey: 'api_store.nav.team',
    icon: mdiAccountGroupOutline,
    activeMatch: 'prefix',
    adminOnly: true,
  },
]);

/**
 * Account pages an API-only user still needs outside the menu: the profile and
 * security pages behind the avatar menu, and the Stripe return page.
 */
const EXTRA_ALLOWED_PATHS = ['/profile', '/account/security', '/stripe/subscribed-callback'];

const ALLOWED_PATHS = [...API_PORTAL_NAV_ITEMS.map((item) => item.to), ...EXTRA_ALLOWED_PATHS];

/** Whether an API-only user may open `path` (exact match or a sub-path). */
export function isApiPortalPath(path) {
  const candidate = String(path || '');
  return ALLOWED_PATHS.some((allowed) => candidate === allowed || candidate.startsWith(`${allowed}/`));
}

/** Menu rows for a user; `isTenantAdmin` unlocks the admin-only rows. */
export function apiPortalNavItems({ isTenantAdmin = false } = {}) {
  return API_PORTAL_NAV_ITEMS.filter((item) => !item.adminOnly || isTenantAdmin);
}
